/**
 * The Shipyard panel model (M6 spec §4 "Buying", plan P27): every player-buyable hull at a shipyard with its class,
 * crew, hold, hardpoints, estimated price and why it can or cannot be bought now.
 *
 * - **Price** mirrors the worker's `orderPrice`: Σ buildCost × S(fill) × priceMargin, with S(fill) the yard's sell
 *   curve per component ware (base × (maxMult + (minMult − maxMult) × fill); a ware the yard holds none of reads
 *   fill 0). The worker charges its own price at purchase, so supply shocks move it as they move this estimate.
 * - **Availability**, in this order: the player's hull gate (M10 P5a1b1, `gates.ts`; off until W6, so no row is
 *   gated while `shipyard.json enabled` is false), the embargo (M10 P4a3b), the fleet cap (50, or the sole trader's cap), the hull's
 *   `standingReq` with the yard's faction, then funds (the wallet while a sole trader, the company account after founding).
 * - **The embargo** (M10 P4a3b, spec §4.8; off while `hull-lines.json embargo.enabled` is false): the same pure
 *   `playerRelationOf`/`playerSurchargeMult` the worker's `buyShipAt` uses, from the personal standing and the
 *   founded company's relation with the owner (`companyRelation`, from the relations view: `companyRelationOf`). War
 *   blocks every hull, skirmish the L/XL hulls; economic relations add the surcharge to the price, so the shown price
 *   is the charged price.
 * - **Fit** (M2's Outfitting rules per ship): a new ship gets the Career's stock fit; its power draw and budget are
 *   M2's power rule for that fit, and the class row gives the weapon and turret hardpoints. Refits go through the
 *   Outfitting tab once the ship is flown.
 * - **Band tabs** (M10 P5a1a, hull tiers spec §6.3): the rows grouped S / M / L / XL (`tabs`, a band with no row has
 *   no tab), each with the maker, role glyph, band, length, the dock-mode line ("Moors: can't enter station bays" for
 *   an L) and the maker's doctrine line. Which hulls are offered is unchanged (`catalog.ts`).
 *
 * - **Gate status lines and the pin** (M10 P5a2a, plan §3.3): while the gates are on (`shipyard.json enabled`) and the
 *   input carries `gate`, a row lists each missing step (`gateLines`: "Tethys standing 25 (you: 12)", "Lease must be
 *   owned", the embargo line) and a locked row can be pinned as the HUD goal (`pinnable`; `pinned` for the hull in
 *   `input.pinnedHull`). Keys off: no line, nothing pinnable, so the shop renders byte-identical.
 *
 * Pure.
 */
import { playerRelationOf, playerSurchargeMult, type EmbargoRules } from '../../../strategic/economy/yardRules'
import { HULL_BAND_IDS, loadHullLines, type HullBand } from '../../../strategic/hullLines'
import { PLAYER_FACTION_ID, RELATION, RELATION_NAMES, type CorpFactionId, type MarketRow, type RelationsView } from '../../../strategic/types'
import { formatCr, formatStanding } from '../../format'
import { factionName } from '../../strategic/names'
import { panelCatalog, type HullOffer, type PanelCatalog } from './catalog'
import { gateHullOf, gateLine, gateStatus, shipyardRules, type GatePlayer, type GateStep, type ShipyardRules } from './gates'
import { errorText } from './outcome'

export interface ShipyardInput {
  /** The yard, or null when this station builds no ships (or its view has not come in). */
  yard: { id: string; name: string; factionName: string; wares: readonly MarketRow[] } | null
  /** The pilot's standing with the yard's faction (−100..100). */
  standing: number
  soleTrader: boolean
  /** What pays: the wallet while a sole trader, the company account after founding. */
  funds: number
  /** Ships in the fleet now and the cap (`FleetView.cap`). */
  fleetSize: number
  cap: number
  /** M2's power rule for the stock fit a new ship gets. */
  stockFit: { draw: number; budget: number }
  /**
   * M10 P4a3b: the founded company's relation with the yard's owner (a `RELATION` code, `companyRelationOf`); −1 or
   * absent while a sole trader or when the owner is outside the ladder (the Authority), so personal standing applies.
   */
  companyRelation?: number
  /**
   * M10 P5a1b1: the player and the yard's owner for the hull gates (`gates.ts`); absent: no row is gated. The gates
   * gate nothing while `shipyard.json enabled` is false.
   */
  gate?: { player: GatePlayer; owner: string }
  /** M10 P5a2a: the hull pinned as the goal (`Career.pinnedHull.hull`), or absent. Read only while the gates are on. */
  pinnedHull?: string
  /** M10 P5a3c: the registry's default name for the next ship (`FleetView.nextName`), shown in the name field; absent: "Registry default". */
  nextName?: string
}

export type ShipyardBlock = 'gate' | 'embargo' | 'cap' | 'standing' | 'funds' | null

/** The shop's embargo data: the switches and each hull's band (`hull-lines.json`, parsed once). */
export interface ShopEmbargo {
  rules: EmbargoRules
  band: ReadonlyMap<string, HullBand>
}

let shopEmbargoMemo: ShopEmbargo | null = null

/** The shipped embargo rules and hull bands (lines, unlined hulls, variants by their base). */
export function shopEmbargo(): ShopEmbargo {
  if (shopEmbargoMemo !== null) return shopEmbargoMemo
  const lines = loadHullLines()
  const band = new Map<string, HullBand>()
  for (const l of lines.lines) for (const h of l.hulls) band.set(h.id, h.band)
  for (const u of lines.unlined) band.set(u.id, u.band)
  for (const v of lines.variants) {
    const b = band.get(v.base)
    if (b !== undefined) band.set(v.id, b)
  }
  shopEmbargoMemo = Object.freeze({ rules: lines.embargo, band })
  return shopEmbargoMemo
}

/**
 * The founded company's relation with yard owner `owner` from the relations view (a `RELATION` code), or −1: while a
 * sole trader, without a view, or when the pair isn't in the ladder (the referee's yards), as the worker's
 * `playerRelation` reads it.
 */
export function companyRelationOf(relations: RelationsView | null, owner: CorpFactionId, soleTrader: boolean): number {
  if (soleTrader || relations === null) return -1
  for (const p of relations.pairs) {
    if ((p.a === PLAYER_FACTION_ID && p.b === owner) || (p.b === PLAYER_FACTION_ID && p.a === owner)) return RELATION_NAMES.indexOf(p.relation)
  }
  return -1
}

/** The player's relation with the yard (`RELATION` code) and whether it refuses `hull`; peace while the embargo is off. */
export function shopRelation(input: Pick<ShipyardInput, 'standing' | 'companyRelation'>, emb: ShopEmbargo = shopEmbargo()): number {
  return playerRelationOf(input.standing, input.companyRelation ?? -1, emb.rules)
}

/** The embargo refuses `hull` at relation `rel`: at war, or at skirmish for a band in `skirmishRefuses`. */
export function embargoRefuses(hull: HullOffer, rel: number, emb: ShopEmbargo = shopEmbargo()): boolean {
  if (rel === RELATION.war) return true
  if (rel !== RELATION.skirmish) return false
  const band = emb.band.get(hull.id)
  return band !== undefined && emb.rules.skirmishRefuses.includes(band)
}

export interface ShipyardRow {
  hull: string
  name: string
  classLabel: string
  specs: string
  price: number
  priceLabel: string
  /** "Needs standing +10 (yours +4)", or null when the hull has no requirement. */
  standingLabel: string | null
  fitLabel: string
  block: ShipyardBlock
  canBuy: boolean
  /** Why it cannot be bought ('' when it can). */
  reason: string
  /** M10 P5a1a: the band tab the row sits in. */
  band: HullBand
  /** The maker mark (a text placeholder: the maker's name), or null on an unlined hull. */
  maker: string | null
  /** The role glyph (a text placeholder, D-052) and label, or null when the hull has no role. */
  roleGlyph: string | null
  roleLabel: string | null
  /** "120 m", or null when no length reads. */
  lengthLabel: string | null
  /** The dock-mode line ("Moors: can't enter station bays"), or null for a bay hull. */
  dockLine: string | null
  /** The maker's doctrine line ("Heavy-duty: +15% hull, −5% firepower"), or null. */
  doctrine: string | null
  /** M10 P5a2a: the missing gate steps and the embargo, one line each; empty while the gates are off. */
  gateLines: string[]
  /** M10 P5a2a: a locked row the player can pin as the HUD goal (gates on, a missing step). */
  pinnable: boolean
  /** M10 P5a2a: this hull is the pinned goal (gates on only). */
  pinned: boolean
}

/** One band tab: its label and its rows, in catalogue order. */
export interface ShipyardTab {
  band: HullBand
  label: string
  rows: ShipyardRow[]
}

export interface ShipyardModel {
  title: string
  subtitle: string
  capLabel: string
  fundsLabel: string
  rows: ShipyardRow[]
  /** M10 P5a1a: the rows by band, S to XL; only bands with a row. */
  tabs: ShipyardTab[]
  /** "No shipyard here." when the station builds no ships. */
  empty: string | null
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

/** The yard's price for `hull` now: Σ buildCost × S(fill) × priceMargin (the worker's `orderPrice`). */
export function hullPrice(hull: HullOffer, wares: readonly MarketRow[], cat: PanelCatalog = panelCatalog()): number {
  let total = 0
  for (const [w, q] of Object.entries(hull.buildCost)) {
    const row = wares.find((r) => r.ware === w)
    const fill = row === undefined ? 0 : clamp01(row.fill)
    const base = cat.basePrice.get(w) ?? 0
    total += q * base * (cat.market.maxMult + (cat.market.minMult - cat.market.maxMult) * fill)
  }
  return total * cat.priceMargin
}

function plural(n: number, one: string): string {
  return `${n} ${one}${n === 1 ? '' : 's'}`
}

/** "Crew 4 · hold 60 t · 4 weapons, 2 turrets". */
export function hullSpecs(h: HullOffer): string {
  const parts = [`Crew ${h.crew}`, `hold ${Math.round(h.cargo)} t`]
  const mounts = [plural(h.cls.weapons, 'weapon')]
  if (h.cls.turrets > 0) mounts.push(plural(h.cls.turrets, 'turret'))
  if (h.cls.utility.length > 0) mounts.push(h.cls.utility.join(', '))
  parts.push(mounts.join(', '))
  return parts.join(' · ')
}

/** The rows grouped by band, S to XL, in catalogue order inside a band; a band with no row has no tab. */
export function shipyardTabs(rows: readonly ShipyardRow[], cat: PanelCatalog = panelCatalog()): ShipyardTab[] {
  const tabs: ShipyardTab[] = []
  for (const band of HULL_BAND_IDS) {
    const inBand = rows.filter((r) => r.band === band)
    if (inBand.length > 0) tabs.push({ band, label: cat.config.shipyard.tabs[band], rows: inBand })
  }
  return tabs
}

/**
 * Why a purchase is refused (the hull gate, embargo, cap, standing, funds), or null. `embargoed`: `embargoRefuses`
 * for this hull; `gated`: the hull gate names a missing step (M10 P5a1b1).
 */
export function shipyardBlock(
  hull: HullOffer,
  price: number,
  input: Pick<ShipyardInput, 'standing' | 'funds' | 'fleetSize' | 'cap'>,
  embargoed = false,
  gated = false,
): ShipyardBlock {
  if (gated) return 'gate'
  if (embargoed) return 'embargo'
  if (input.fleetSize >= input.cap) return 'cap'
  if (hull.standingReq !== null && !(input.standing >= hull.standingReq)) return 'standing'
  if (!(input.funds >= price)) return 'funds'
  return null
}

/** The missing gate steps of `hull` (M10 P5a1b1): none without `input.gate`, for a hull with no band, or while off. */
export function shopGateMissing(hull: HullOffer, input: Pick<ShipyardInput, 'standing' | 'gate'>, rules: ShipyardRules = shipyardRules()): GateStep[] {
  const g = input.gate
  if (g === undefined || !rules.enabled) return []
  const row = gateHullOf(hull.id)
  if (row === null) return []
  return gateStatus(g.player, row, { owner: g.owner, standing: input.standing }, rules).missing
}

function reasonText(block: ShipyardBlock, hull: HullOffer, price: number, input: ShipyardInput, factionName: string, missing: readonly GateStep[] = []): string {
  switch (block) {
    case 'gate':
      return `Locked: ${missing.map((m) => gateLine(m, nameOfFaction)).join('; ')}.`
    case 'embargo':
      return errorText('embargo')
    case 'cap':
      return input.soleTrader
        ? `Fleet full (${input.fleetSize}/${input.cap}): found a corporation to own more ships.`
        : `Fleet full (${input.fleetSize}/${input.cap}).`
    case 'standing':
      return `Needs standing ${formatStanding(hull.standingReq ?? 0)} with ${factionName} (yours ${formatStanding(input.standing)}).`
    case 'funds':
      return `Not enough credits: ${formatCr(price)} needed, ${input.soleTrader ? 'wallet' : 'company account'} has ${formatCr(input.funds)}.`
    case null:
      return ''
  }
}

/** "12,345 cr", plus the economic surcharge when it applies ("12,345 cr (incl. 15% surcharge)"). */
function priceLabelOf(price: number, mult: number): string {
  if (mult === 1) return formatCr(price)
  return `${formatCr(price)} (incl. ${Math.round((mult - 1) * 100)}% surcharge: strained relations)`
}

/** A gate line's faction name: the maker's short name ("Tethys"), else the faction's ("Reach Charter Authority"). */
export function nameOfFaction(id: string): string {
  const m = loadHullLines().makers as Partial<Record<string, { name: string }>>
  return m[id]?.name ?? factionName(id)
}

/** "Embargo: Varro Security at war with you" (war), "… in a skirmish with you" (skirmish), or null. */
export function embargoLine(rel: number, factionName: string): string | null {
  if (rel === RELATION.war) return `Embargo: ${factionName} at war with you`
  if (rel === RELATION.skirmish) return `Embargo: ${factionName} in a skirmish with you`
  return null
}

/** M10 P5a2a: a row's status lines (gate steps, then the embargo); none while the gates are off or without `gate`. */
export function gateLinesOf(missing: readonly GateStep[], embargoed: boolean, rel: number, factionName: string, on: boolean): string[] {
  if (!on) return []
  const lines = missing.map((m) => gateLine(m, nameOfFaction))
  const emb = embargoed ? embargoLine(rel, factionName) : null
  if (emb !== null) lines.push(emb)
  return lines
}

export function shipyardModel(input: ShipyardInput, cat: PanelCatalog = panelCatalog(), emb: ShopEmbargo = shopEmbargo(), rules: ShipyardRules = shipyardRules()): ShipyardModel {
  const yard = input.yard
  const payer = input.soleTrader ? 'Wallet' : 'Company account'
  const base = {
    capLabel: `Fleet ${input.fleetSize}/${input.cap}`,
    fundsLabel: `${payer}: ${formatCr(input.funds)}`,
  }
  if (yard === null)
    return {
      title: 'Shipyard',
      subtitle: '',
      ...base,
      rows: [],
      tabs: [],
      empty: 'No shipyard here.',
    }
  const text = cat.config.shipyard
  const fitPower = `stock fit, power ${Math.round(input.stockFit.draw)}/${Math.round(input.stockFit.budget)}`
  const fitOver = input.stockFit.draw > input.stockFit.budget
  const rel = shopRelation(input, emb)
  const gatesOn = rules.enabled && input.gate !== undefined
  const mult = playerSurchargeMult(rel, emb.rules)
  const rows = cat.hulls.map((h): ShipyardRow => {
    const price = Math.round(hullPrice(h, yard.wares, cat) * mult)
    const missing = shopGateMissing(h, input, rules)
    const embargoed = embargoRefuses(h, rel, emb)
    const block = shipyardBlock(h, price, input, embargoed, missing.length > 0)
    return {
      hull: h.id,
      name: h.name,
      classLabel: h.classLabel,
      specs: hullSpecs(h),
      price,
      priceLabel: priceLabelOf(price, mult),
      standingLabel: h.standingReq === null ? null : `Needs standing ${formatStanding(h.standingReq)}`,
      fitLabel: `Delivered with the ${fitPower}${fitOver ? ' (over budget)' : ''}; refit at Outfitting.`,
      block,
      canBuy: block === null,
      reason: reasonText(block, h, price, input, yard.factionName, missing),
      band: h.band,
      maker: h.maker,
      roleGlyph: h.role === null ? null : text.roles[h.role].glyph,
      roleLabel: h.role === null ? null : text.roles[h.role].label,
      lengthLabel: h.lengthM === null ? null : `${Math.round(h.lengthM)} ${text.lengthUnit}`,
      dockLine: text.dockLines[h.dockMode] ?? null,
      doctrine: h.doctrine,
      gateLines: gateLinesOf(missing, embargoed, rel, yard.factionName, gatesOn),
      pinnable: gatesOn && missing.length > 0,
      pinned: gatesOn && input.pinnedHull === h.id,
    }
  })
  return {
    title: `${yard.name} shipyard`,
    subtitle: `${yard.factionName} yard · prices follow its component stock`,
    ...base,
    rows,
    tabs: shipyardTabs(rows, cat),
    empty: null,
  }
}

/** The status line after a purchase. */
export function boughtText(name: string): string {
  return `${name} ordered: it joins the fleet when the yard finishes it.`
}
